import type BarcodeScannerResult from './barcodeScannerResultInterface'

let scanServiceInstance: ReturnType<typeof createScanService> | null = null

const createScanService = () => {
  const logPrefix = '[SCR:Utils] BarcodeScan:'
  const config = {
    treshold: 30,
    prefix: 'space',
    regex: {
      pattern: /^[A-Za-z0-9.\-%]$/,
      flags: 'i'
    }
  }
  const scanResult = {
    code: '',
    finished: false
  } // Code to be passed to ticket check
  let currentTime = 0 // time current key is received (time based scanning)
  const previousKey: { receivedAt: null | number; event: null | KeyboardEvent } = {
    receivedAt: null,
    event: null
  }
  let previousEventReceived = Date.now() // time when previous event was received (time based scanning)
  let difference = 0
  let inScanMode = false
  let secureTimerOff: number
  let cb: null | ((res: BarcodeScannerResult) => void) = null

  function isPrefixBased(e: KeyboardEvent) {
    return e.code?.toLowerCase() === config.prefix
  }

  function isScanModeActive() {
    return inScanMode || scanResult.code.length >= 2
  }

  function processKeyEvent(e: KeyboardEvent) {
    const whitelistedKeys = new RegExp(config.regex.pattern, config.regex.flags) // Array of key codes whose values wont't be concat with ticketId (enter, shift, space, arrow down)
    const isPrefixTriggered = isPrefixBased(e)
    let treshold = config.treshold

    currentTime = Date.now()
    difference = currentTime - previousEventReceived
    previousEventReceived = currentTime

    console.debug(`${logPrefix} Received key ${e.key}`)

    if (inScanMode) {
      treshold += 100
      console.debug(`${logPrefix} Set treshold to ${treshold}`)
    }

    // Too much difference between characters - which means that this is not scan mode.
    // First time, difference is equal to current time, so we will extract that from check.
    // We also prevent scan trigger if somone holds key (e.repeat).
    // Last flag is when space is trigered as first char, we want to enter in scan mode
    if ((difference > treshold && difference !== currentTime && !isPrefixTriggered) || e.repeat) {
      console.debug(`${logPrefix} It\`s not scan mode. Reseting..`, {
        key: e.key,
        code: e.code,
        difference,
        currentTime,
        previousEventReceived
      })

      scanResult.code = ''
      previousKey.receivedAt = currentTime
      previousKey.event = e
      scanResult.finished = false
      console.debug(`${logPrefix} Reset.`, difference, isPrefixTriggered, treshold)

      return scanResult
    }

    console.debug(`${logPrefix} Possible scan mode.`)

    if (isPrefixTriggered) {
      console.debug(`${logPrefix} Space triggered and scan mode marked as started.`)
      // in case when we recive space we want to strip any previous char,
      // this will happen if we have scanner with hardcoded prefix (e.g. ctrl+b)
      scanResult.code = ''
      inScanMode = true
      secureTimerOff = window.setTimeout(function () {
        inScanMode = false
        console.debug(`${logPrefix} Secure timer off triggered.`)
      }, 2000)

      return scanResult
    }

    // let's add previous char to list of scanned codes if time passed
    // before previous and current is below treshold
    // this will happen if scan barcode without space prefix
    if (
      scanResult.code.length === 0 &&
      !isPrefixTriggered &&
      previousKey.event &&
      previousKey.receivedAt &&
      currentTime - previousKey.receivedAt < treshold * 2 &&
      whitelistedKeys.test(previousKey.event.key)
    ) {
      console.debug(`${logPrefix} Append previous key.`, previousKey.event.code, previousKey.event.key)

      scanResult.code += previousKey.event.key
      previousKey.event = null
      previousKey.receivedAt = 0
    }

    // prevent native keydown behavior if we detected space
    // or we are in time based scan (at least two chars are added to final result)
    if (isScanModeActive()) {
      console.debug(`${logPrefix} Prevent keydown default behavior.`)
      e.preventDefault()
      e.stopImmediatePropagation()
      e.stopPropagation()
    }

    // we need moove focus out of any input to body so we don't enter
    // codes from scaner but we don't know if first char is from scan so don't move focus when first code is entered
    // codes from scaner (e.g we could trigger enter on payin)
    // but we don't know if first char is from scan so don't move focus when first code is entered
    if (isScanModeActive() && document.activeElement && document.activeElement.tagName.toLocaleLowerCase() !== 'body') {
      ;(document.activeElement as HTMLElement).blur()
    }

    if (whitelistedKeys.test(e.key)) {
      console.debug(`${logPrefix} Adding key.`, e.code, e.key)
      scanResult.code += e.key
      previousKey.event = null
    }

    if (e.key?.toLowerCase() === 'enter') {
      if (scanResult.code.length) {
        scanResult.finished = true
        inScanMode = false
        clearTimeout(secureTimerOff)
      }
    }

    return scanResult
  }

  function timeBasedScanning(e: KeyboardEvent) {
    const result = processKeyEvent(e)
    if (result.finished) {
      const eventData = {
        callSign: {
          sufix: null
        },
        data: {
          code: result.code
        }
      }

      if (cb) {
        console.debug(`${logPrefix} Scan finished. Executing callback`, eventData)
        cb(eventData)
      }

      scanResult.code = ''
      scanResult.finished = false
    }
  }

  const shouldPropagateScanKey = (e: KeyboardEvent) => {
    const propagatedKeys = ['escape', '+', ...Array.from({ length: 10 }, (_, i) => `f${i + 1}`)]

    return propagatedKeys.includes(e.key?.toLowerCase())
  }

  function listenForScannerEvents(resultCb: (res: BarcodeScannerResult) => void) {
    cb = resultCb
    document.addEventListener('keydown', timeBasedScanning)
  }

  function stopListeningForScannerEvents() {
    cb = null
    document.removeEventListener('keydown', timeBasedScanning)
  }

  return {
    listenForScannerEvents,
    stopListeningForScannerEvents,
    isScanActive: () => inScanMode,
    shouldPropagateScanKey
  }
}

const ScanService = () => {
  if (!scanServiceInstance) {
    scanServiceInstance = createScanService()
  }

  return scanServiceInstance
}

export default ScanService
